import type { TiptapDoc } from '@odo/shared-ui';
import type { CommunicationEntry } from '../../../../services/communications';

interface TiptapNode {
    type?: string;
    text?: string;
    attrs?: Record<string, unknown>;
    content?: TiptapNode[];
}

const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

function stripImages(nodes: TiptapNode[] | undefined): TiptapNode[] {
    if (!nodes) return [];
    return nodes
        .filter((node) => node.type !== 'image' && node.type !== 'imageGrid')
        .map((node) =>
            Array.isArray(node.content) ? { ...node, content: stripImages(node.content) } : node
        );
}

// A block-level image sitting between two paragraphs of text often leaves an
// empty paragraph behind once it's filtered out - either the blank line the
// author left around it, or one the editor auto-inserts below a block node.
// Left in, it renders as a stray gap (`.note-rich-body p + p` adds margin
// between every top-level block) that a hand-typed note never has.
function isEmptyParagraph(node: TiptapNode): boolean {
    if (node.type !== 'paragraph') return false;
    if (!node.content || node.content.length === 0) return true;
    return node.content.every((child) => child.type === 'text' && !(child.text ?? '').trim());
}

/** Communication images live in the thread's own storage scope - carrying
 *  them into a patient note would render broken there. Also drops any empty
 *  paragraph left at the top level (leading, trailing, or where an image
 *  used to sit) - never inside a list item or similar, where an empty
 *  paragraph is structural, not blank-line filler. Immutable. */
export function stripImageNodes(doc: TiptapDoc): TiptapDoc {
    const withoutImages = stripImages(doc.content as TiptapNode[]);
    return { ...doc, content: withoutImages.filter((node) => !isEmptyParagraph(node)) };
}

/** One paragraph holding the whole string - not one per line. */
function singleParagraphDoc(text: string): TiptapDoc {
    return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] };
}

/** Seeds a new patient note's body from the interaction it's created from. */
export function patientNoteDocFromEntry(entry: CommunicationEntry): TiptapDoc {
    if (entry.bodyDoc) return stripImageNodes(entry.bodyDoc);
    if (entry.body && entry.body.trim().length > 0) return singleParagraphDoc(entry.body);
    return EMPTY_DOC;
}
